'use client';

import type { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import type { ImageValidationReason } from '@/app/utils/client';
import { truncateUploadErrorList } from './upload-error-toast-format';

type Translator = ReturnType<typeof useTranslations>;

export interface ImageRejectedItem {
  name: string;
  reason: ImageValidationReason;
}

/**
 * claw 上传链路里"图片拒绝（imageRejected）" 与"压缩失败（imageCompressFailed）"两类批量
 * toast 的统一封装。任何 claw 直传 / 转正 / 补传分支都应通过这里发 toast，
 * 避免出现"压缩失败合并到通用上传失败"或"压缩失败静默丢弃"这类口径漂移。
 *
 * - 校验拒绝（reason 多种）走 imageRejected.aggregate，去重后用 ' / ' 拼 reason
 * - 压缩失败统一走 imageCompressFailed，列表用 truncateUploadErrorList 与公共上传层保持一致
 */
export const emitImageRejectedToastWith = (t: Translator, imageRejected: readonly ImageRejectedItem[]): void => {
  if (!imageRejected.length) return;
  const seen = new Set<ImageValidationReason>();
  const reasonLabels: string[] = [];
  for (const item of imageRejected) {
    if (seen.has(item.reason)) continue;
    seen.add(item.reason);
    reasonLabels.push(t(`claw.upload.imageRejected.reasons.${item.reason}`));
  }
  toast({
    type: 'error',
    message: t('claw.upload.imageRejected.aggregate', {
      count: imageRejected.length,
      reasons: reasonLabels.join(' / '),
    }),
  });
};

export const emitImageCompressFailedToastWith = (t: Translator, failedNames: readonly string[]): void => {
  if (!failedNames.length) return;
  toast({
    type: 'error',
    message: `${t('claw.upload.imageCompressFailed', {
      count: failedNames.length,
    })}${truncateUploadErrorList([...failedNames])}`,
  });
};

/**
 * 组件 / hook 内推荐使用的 hook 版包装：自动绑定 useTranslations 实例，且每次渲染保持
 * 引用稳定（依赖项只有 t）。
 *
 * 在没有 hook 上下文的纯函数（例如本目录下的 emitBatchToast）里直接用 emitImageXxxToastWith。
 */
export const useClawImageToasts = (t: Translator) => {
  const emitImageRejectedToast = useCallback(
    (imageRejected: readonly ImageRejectedItem[]) => emitImageRejectedToastWith(t, imageRejected),
    [t],
  );
  const emitImageCompressFailedToast = useCallback(
    (failedNames: readonly string[]) => emitImageCompressFailedToastWith(t, failedNames),
    [t],
  );
  return { emitImageRejectedToast, emitImageCompressFailedToast };
};
